import {IconButton, Typography} from '@mui/material';
import React from 'react';
import {t} from 'i18next';
import AurButton from '../../../common/aur-button/AurButton';
import {getUTCDateTimeForDisplay} from '../../../../utils/utils';
import {ReactComponent as CloseIcon} from '../../../../assests/svg/close-round.svg';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import {Project} from '../../welcome-screen-single-client/model';
import {
  clearUploadEvidenceSource,
  setSelectedProjectForEvidenceSource,
} from '../../../../redux/slices/uploadEvidenceSourceSlice';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  PROJECT_CREATION_PATH,
  setProjectCreationInitiatedFrom,
  setClientIdForProjectCreation,
} from '../../../../redux/slices/projectCreationSlice';
import navConstants from '../../../../constants/navConstants';
import {ONLY_DATE_SHOW_FORMAT} from '../../../../constants/constants';
import {RootState} from '../../../../redux/store';

interface ProjectRowProps {
  projectDetails: Project;
  onProjectRemoveClick?: () => void;
}

const AurProjectStatusRow: React.FC<ProjectRowProps> = ({
  projectDetails,
  onProjectRemoveClick,
}) => {
  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const {canUploadEvidence} = usePermissionsByPage(
    PAGE_NAME.PROJECT_OVERVIEW,
    projectDetails.projectId
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isPending = projectDetails?.projectStatus === 'Pending';
  const stringPath = 'common.project.';

  const handleViewSetupStatus = () => {
    dispatch(
      setProjectCreationInitiatedFrom(PROJECT_CREATION_PATH.ClientDetails)
    );

    const queryParam = {
      id: projectDetails.projectId,
      name: projectDetails.projectName,
    };
    dispatch(setClientIdForProjectCreation(clientId));
    navigate(
      navConstants.projectCreation.progressDetail +
        `?${new URLSearchParams(queryParam).toString()}`
    );
  };

  const handleUploadEvidenceSource = () => {
    dispatch(clearUploadEvidenceSource());
    dispatch(
      setSelectedProjectForEvidenceSource({
        projectAlphaCode: projectDetails.projectAlphaCode,
        projectId: projectDetails.projectId,
        projectName: projectDetails.projectName,
        isNonConsilioWorkflow: projectDetails.isNonConsilioWorkflow,
      })
    );
    navigate('/upload-evidence/new-evidence');
  };

  return (
    <div className="projectStatus-section">
      <div className="project-row">
        {!isPending && (
          <IconButton
            size="small"
            onClick={onProjectRemoveClick}
            edge="end"
            className="project-remove-icon"
            tabIndex={0}
            aria-label={t(stringPath + 'removeIconAriaLabel')}
          >
            <CloseIcon />
          </IconButton>
        )}
        <div className="row-wrap align-items-center">
          <Typography
            className={`typo-text subhead5_size label ${
              isPending ? 'pending-p' : 'r-added'
            }`}
          >
            {t(
              stringPath +
                'projectRowStatus.' +
                (isPending ? 'pending' : 'recentlyAdded')
            )}
          </Typography>

          <span className="project-name typo-text body3_size">
            {projectDetails?.projectName}
          </span>

          <span className="project-date typo-text body3_size">
            {t(stringPath + (isPending ? 'submitted' : 'added'))}{' '}
            <AurLightTooltip title={t('common.yearMonthDate')}>
              <span>
                {getUTCDateTimeForDisplay(
                  projectDetails?.createdOn,
                  ONLY_DATE_SHOW_FORMAT
                )}
              </span>
            </AurLightTooltip>
          </span>
        </div>
        {isPending ? (
          <AurButton
            type="text"
            size="Small"
            title={t(stringPath + 'viewSetupStatus')}
            aria-label={t(stringPath + 'viewSetupStatus')}
            onClick={handleViewSetupStatus}
            label={t(stringPath + 'viewSetupStatus')}
            dataTestId="view-setup-status-button"
            classNames="view-status-button"
          />
        ) : (
          canUploadEvidence && (
            <AurButton
              type="continue"
              size="Small"
              title={t(stringPath + 'uploadEvidenceSource')}
              aria-label={t(stringPath + 'uploadEvidenceSource')}
              onClick={handleUploadEvidenceSource}
              label={t(stringPath + 'uploadEvidenceSource')}
              dataTestId="upload-evidence-source-button"
            />
          )
        )}
      </div>
    </div>
  );
};

export default AurProjectStatusRow;
